<template>
  <div class="yt-grid">
    <yt-chart-card
      class="yt-span-8"
      title="Vues et revenus venus de Discover"
      :subtitle="`Vues et revenus attribués à Google Discover, par ${granularityLabel} (estimation).`"
      :sources="['gsc', 'yt', 'calc']"
      :columns="discoverColumns"
      :rows="buckets"
      :loading="!overview"
      :empty="!hasDiscover"
      empty-text="Aucune vue venue de Discover pour ces filtres sur la période."
    >
      <div class="yt-panels">
        <vue-apex-charts type="bar" height="150" :options="panelOptions('Vues venues de Discover', fInt, 'bar')" :series="[{ name: 'Vues venues de Discover', data: buckets.map((b) => b.discover_views) }]" />
        <vue-apex-charts type="line" height="150" :options="panelOptions('Part des vues venue de Discover', (v) => fPct(v), 'line')" :series="[{ name: 'Part des vues', data: buckets.map((b) => Number((b.discover_views_share * 100).toFixed(2))) }]" />
        <vue-apex-charts type="bar" height="150" :options="panelOptions('Revenus venus de Discover', fEur2, 'bar', fEurAxis)" :series="[{ name: 'Revenus venus de Discover', data: buckets.map((b) => Number(b.discover_revenue.toFixed(2))) }]" />
      </div>
    </yt-chart-card>

    <yt-chart-card
      class="yt-span-4"
      :title="`Part de Discover par ${breakdownLevel}`"
      subtitle="Vues et revenus venus de Discover, et leur part (estimation)."
      :sources="['calc']"
      :loading="loading"
      :empty="breakdownRows.length === 0"
    >
      <table class="yt-table">
        <thead>
          <tr><th>{{ breakdownLevelCap }}</th><th class="yt-table__num">Vues Discover</th><th class="yt-table__num">Part</th><th class="yt-table__num">Revenus Discover</th><th class="yt-table__num">Part</th></tr>
        </thead>
        <tbody>
          <tr v-for="row in breakdownRows" :key="row.id">
            <td>{{ row.label }}</td>
            <td class="yt-table__num"><span class="yt-ibar"><i :style="{ width: `${(row.discover_views / breakdownMax) * 100}%` }"></i>{{ fBig(row.discover_views) }}</span></td>
            <td class="yt-table__num">{{ fPct(row.discover_views_share) }}</td>
            <td class="yt-table__num">{{ fEur(row.discover_revenue) }}</td>
            <td class="yt-table__num">{{ fPct(row.discover_revenue_share) }}</td>
          </tr>
        </tbody>
      </table>
    </yt-chart-card>

    <yt-chart-card
      class="yt-span-6"
      title="Top 10 des vidéos"
      subtitle="Cliquez sur un titre pour ouvrir la fiche de la vidéo."
      :sources="['yt']"
      :columns="topColumns"
      :rows="topVideos"
      :loading="loading"
      :empty="topVideos.length === 0"
    >
      <template #tools>
        <rmr-segmented-control :value="topMetric" :items="topMetricItems" size="sm" @input="onTopMetric" />
      </template>
      <ol class="yt-top">
        <li v-for="(video, index) in topVideos" :key="video.video_id" class="yt-top__row" @click="$emit('open-video', video.video_id)">
          <span class="yt-top__rank">{{ index + 1 }}</span>
          <img v-if="video.thumbnail_url" :src="video.thumbnail_url" alt="" class="yt-top__thumb" loading="lazy" />
          <span class="yt-top__main">
            <span class="yt-top__title">{{ video.title }}</span>
            <span class="yt-top__meta">{{ video.site_name }} · {{ fDayLong(video.published_at) }}</span>
            <span class="yt-top__bar"><i :style="{ width: `${(video[topMetric] / topMax) * 100}%` }"></i></span>
          </span>
          <span class="yt-top__value">{{ topFormat(video[topMetric]) }}</span>
        </li>
      </ol>
    </yt-chart-card>

    <yt-chart-card
      class="yt-span-3"
      title="Sources de trafic"
      subtitle="Part des vues YouTube, Google Discover compris (chaînes du périmètre, toutes vidéos classiques)."
      :sources="['yt', 'calc']"
      :columns="sourceColumns"
      :rows="trafficSources"
      :loading="loading"
      :empty="trafficSources.length === 0"
    >
      <vue-apex-charts type="donut" height="260" :options="donutOptions" :series="trafficSources.map((s) => s.views)" />
      <ul class="yt-legend">
        <li v-for="(source, index) in trafficSources.slice(0, 6)" :key="source.source_type">
          <i class="yt-key" :style="{ background: palette[index % palette.length] }"></i>
          <span>{{ sourceTypeLabel(source.source_type) }}</span>
          <b>{{ fPct(source.share) }}</b>
        </li>
      </ul>
    </yt-chart-card>

    <yt-chart-card
      class="yt-span-3"
      title="Top sources externes"
      subtitle="Vues venues de l'extérieur de YouTube, hors Discover."
      :sources="['yt']"
      :columns="externalColumns"
      :rows="externalSources"
      :loading="loading"
      :empty="externalSources.length === 0"
    >
      <ul class="yt-bars">
        <li v-for="source in externalSources" :key="source.source">
          <span class="yt-bars__label">{{ source.source }}</span>
          <span class="yt-bars__track"><i :style="{ width: `${(source.views / externalMax) * 100}%` }"></i></span>
          <span class="yt-bars__value">{{ fBig(source.views) }}</span>
        </li>
      </ul>
    </yt-chart-card>
  </div>
</template>

<script>
import { computed, ref, toRefs } from '@vue/composition-api'
import VueApexCharts from 'vue-apexcharts'
import RmrSegmentedControl from '@/components/ui/actions/RmrSegmentedControl.vue'
import YtChartCard from '../YtChartCard.vue'
import useYoutubePerf from '../useYoutubePerf'
import {
  COLORS, GRANULARITY_LABELS, baseChartOptions, bucketLabel, bucketTitle, fBig, fCompact, fDayLong, fEur, fEur2, fEurAxis, fInt, fPct,
  sourceTypeLabel,
} from '../ytFormat'

const TOP_METRICS = [
  { value: 'views', label: 'Vues', format: fInt },
  { value: 'revenue', label: 'Revenus', format: fEur },
  { value: 'likes', label: 'Likes', format: fInt },
  { value: 'shares', label: 'Partages', format: fInt },
]

export default {
  name: 'YtVideosTab',
  components: { VueApexCharts, RmrSegmentedControl, YtChartCard },
  props: { active: { type: Boolean, default: false } },
  setup(props) {
    const { active } = toRefs(props)
    const { store, get, ensureOverview, useTabLoader } = useYoutubePerf()

    const traffic = ref(null)
    const loading = ref(false)
    const topMetric = computed(() => store.getters['youtubePerf/getTopMetric'])

    const fetchTraffic = async () => {
      loading.value = true
      try {
        const { data } = await get('/traffic', { top_metric: topMetric.value })
        traffic.value = data
      } finally {
        loading.value = false
      }
    }
    const { reload } = useTabLoader(active, () => Promise.all([ensureOverview(), fetchTraffic()]))
    const onTopMetric = (metric) => {
      store.dispatch('youtubePerf/updateTopMetric', metric)
      reload()
    }

    const overview = computed(() => store.getters['youtubePerf/getOverview'])
    const buckets = computed(() => overview.value?.series?.buckets || [])
    const granularity = computed(() => overview.value?.series?.granularity || 'day')
    const granularityLabel = computed(() => GRANULARITY_LABELS[granularity.value])
    const hasDiscover = computed(() => (overview.value?.totals?.discover_views || 0) > 0)
    const categories = computed(() => buckets.value.map((b) => bucketLabel(b, granularity.value)))
    const titles = computed(() => buckets.value.map((b) => bucketTitle(b, granularity.value)))

    const panelOptions = (name, formatter, type, axisFormatter) => ({
      ...baseChartOptions(),
      chart: { ...baseChartOptions().chart, type, sparkline: { enabled: false } },
      colors: [COLORS.discover],
      title: { text: name, style: { fontSize: '12px', fontWeight: 600, color: '#475569' } },
      stroke: { width: type === 'line' ? 2 : 0, curve: 'smooth' },
      plotOptions: { bar: { borderRadius: 3, columnWidth: buckets.value.length > 40 ? '90%' : '60%' } },
      xaxis: { ...baseChartOptions().xaxis, categories: categories.value, labels: { show: false } },
      yaxis: { labels: { style: { colors: '#94A3B8', fontSize: '10px', fontWeight: 600 }, formatter: (v) => (axisFormatter || (type === 'line' ? (x) => `${Math.round(x)} %` : fCompact))(v) }, min: 0, forceNiceScale: true },
      tooltip: { x: { formatter: (_, { dataPointIndex }) => titles.value[dataPointIndex] || '' }, y: { formatter: (v) => (type === 'line' ? formatter(v / 100) : formatter(v)) } },
    })
    const discoverColumns = [
      { key: 'label', label: 'Période', numeric: false, format: (_, row) => bucketTitle(row, granularity.value) },
      { key: 'discover_views', label: 'Vues venues de Discover', format: fInt },
      { key: 'discover_views_share', label: 'Part des vues', format: (v) => fPct(v) },
      { key: 'discover_revenue', label: 'Revenus venus de Discover', format: fEur },
      { key: 'discover_revenue_share', label: 'Part des revenus', format: (v) => fPct(v) },
    ]

    const breakdownRows = computed(() => (traffic.value?.breakdown?.rows || []).filter((row) => row.discover_views > 0 || row.discover_impressions > 0).sort((a, b) => b.discover_views - a.discover_views))
    const breakdownMax = computed(() => Math.max(1, ...breakdownRows.value.map((row) => row.discover_views)))
    const breakdownLevel = computed(() => (traffic.value?.breakdown?.level === 'site' ? 'site' : 'univers'))
    const breakdownLevelCap = computed(() => (breakdownLevel.value === 'site' ? 'Site' : 'Univers'))

    const topVideos = computed(() => traffic.value?.top_videos || [])
    const topMax = computed(() => Math.max(1, ...topVideos.value.map((video) => Number(video[topMetric.value] || 0))))
    const topMetricItems = TOP_METRICS.map(({ value, label }) => ({ value, label }))
    const topFormat = computed(() => TOP_METRICS.find((m) => m.value === topMetric.value)?.format || fInt)
    const topColumns = computed(() => [
      { key: 'title', label: 'Vidéo', numeric: false },
      { key: 'site_name', label: 'Site', numeric: false },
      { key: 'published_at', label: 'Publication', numeric: false, format: fDayLong },
      { key: topMetric.value, label: TOP_METRICS.find((m) => m.value === topMetric.value)?.label, format: topFormat.value },
    ])

    const trafficSources = computed(() => traffic.value?.traffic_sources || [])
    const palette = COLORS.palette
    const donutOptions = computed(() => ({
      chart: { type: 'donut', fontFamily: 'Inter, system-ui, sans-serif', animations: { enabled: false } },
      colors: palette,
      labels: trafficSources.value.map((s) => sourceTypeLabel(s.source_type)),
      plotOptions: { pie: { donut: { size: '70%', labels: { show: true, name: { show: true, fontSize: '11px', fontWeight: 600, color: '#64748B' }, value: { show: true, fontSize: '18px', fontWeight: 800, color: '#0F172A', formatter: (v) => fBig(v) }, total: { show: true, label: 'Vues', fontSize: '11px', color: '#94A3B8', formatter: (w) => fBig(w.globals.seriesTotals.reduce((a, b) => a + b, 0)) } } } } },
      dataLabels: { enabled: false },
      legend: { show: false },
      stroke: { width: 2, colors: ['#fff'] },
      tooltip: { y: { formatter: (v) => fInt(v) } },
    }))
    const sourceColumns = [
      { key: 'source_type', label: 'Source', numeric: false, format: sourceTypeLabel },
      { key: 'views', label: 'Vues', format: fInt },
      { key: 'share', label: 'Part', format: (v) => fPct(v) },
    ]

    const externalSources = computed(() => traffic.value?.external_sources || [])
    const externalMax = computed(() => Math.max(1, ...externalSources.value.map((s) => s.views)))
    const externalColumns = [
      { key: 'source', label: 'Source externe', numeric: false },
      { key: 'views', label: 'Vues', format: fInt },
      { key: 'share', label: 'Part du top', format: (v) => fPct(v) },
    ]

    return {
      overview, buckets, granularityLabel, hasDiscover, loading, panelOptions, discoverColumns,
      breakdownRows, breakdownMax, breakdownLevel, breakdownLevelCap,
      topVideos, topMax, topMetric, topMetricItems, topFormat, topColumns, onTopMetric,
      trafficSources, palette, donutOptions, sourceColumns, externalSources, externalMax, externalColumns,
      fBig, fInt, fEur, fEur2, fEurAxis, fPct, fDayLong, sourceTypeLabel,
    }
  },
}
</script>

<style lang="scss" scoped>
.yt-grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: $rmr-space-lg;
}
.yt-span-3 { grid-column: span 3; }
.yt-span-4 { grid-column: span 4; }
.yt-span-6 { grid-column: span 6; }
.yt-span-8 { grid-column: span 8; }
@media (max-width: 1100px) {
  .yt-span-3 { grid-column: span 6; }
  .yt-span-4, .yt-span-8, .yt-span-6 { grid-column: span 12; }
}
@media (max-width: 760px) { .yt-grid > * { grid-column: span 12; } }

.yt-panels { display: flex; flex-direction: column; gap: 2px; }

.yt-ibar {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
  i { display: inline-block; height: 8px; min-width: 2px; max-width: 80px; width: 80px; border-radius: 4px; background: $rmr-indigo-light; position: relative; }
}

.yt-top { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; }
.yt-top__row {
  display: grid;
  grid-template-columns: 22px 64px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  padding: 7px 4px;
  border-bottom: 1px solid $rmr-border;
  cursor: pointer;
  &:hover { background: $rmr-indigo-bg; }
}
.yt-top__rank { font-size: $rmr-fs-sm; font-weight: 700; color: $rmr-text-subtle; text-align: center; }
.yt-top__thumb { width: 64px; height: 36px; object-fit: cover; border-radius: 6px; background: $rmr-surface-2; }
.yt-top__main { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.yt-top__title { font-size: $rmr-fs-base; font-weight: 600; color: $rmr-text-primary; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.yt-top__meta { font-size: $rmr-fs-xs; color: $rmr-text-subtle; }
.yt-top__bar { display: block; height: 4px; border-radius: 2px; background: $rmr-surface-2; overflow: hidden; i { display: block; height: 100%; background: $rmr-indigo; border-radius: 2px; } }
.yt-top__value { font-size: $rmr-fs-md; font-weight: 700; font-variant-numeric: tabular-nums; color: $rmr-text-primary; }

.yt-legend { list-style: none; margin: 8px 0 0; padding: 0; display: flex; flex-direction: column; gap: 4px; font-size: $rmr-fs-sm; color: $rmr-text-secondary;
  li { display: flex; align-items: center; gap: 8px; span { flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; } b { font-variant-numeric: tabular-nums; } }
}
.yt-key { display: inline-block; width: 10px; height: 10px; border-radius: 3px; flex: none; }

.yt-bars { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px;
  li { display: grid; grid-template-columns: minmax(0, 1fr) 90px auto; gap: 8px; align-items: center; font-size: $rmr-fs-sm; }
}
.yt-bars__label { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: $rmr-text-secondary; }
.yt-bars__track { display: block; height: 8px; border-radius: 4px; background: $rmr-surface-2; overflow: hidden; i { display: block; height: 100%; background: $rmr-sessions; border-radius: 4px; } }
.yt-bars__value { font-weight: 700; font-variant-numeric: tabular-nums; color: $rmr-text-primary; }
</style>
